import { Route } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import ForAgentsPage from "@/pages/ForAgentsPage";
import ForTeamsLandingPage from "@/pages/ForTeamsLandingPage";
import AboutPage from "@/pages/AboutPage";
import PressPage from "@/pages/PressPage";
import PressReleasePage from "@/pages/PressReleasePage";
import PricingPage from "@/pages/PricingPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import WaitlistPage from "@/pages/WaitlistPage";
import BrokerageDirectoryPage from "@/pages/BrokerageDirectoryPage";
import { DIRECTORY_BASE_PATH, directoryPaths } from "@/lib/brokerageDirectory";

/**
 * The public marketing + legal routes — the ones that get prerendered to static
 * HTML at build time (scripts/prerender.mjs).
 *
 * Single source of truth, consumed twice:
 *   1. src/App.tsx renders it inside the app's <Routes>, so these paths behave
 *      exactly as before in the SPA.
 *   2. src/entry-prerender.tsx renders it under a StaticRouter in Node, one
 *      route at a time, to produce the crawler-visible HTML.
 *
 * Anything behind ProtectedRoute / FeatureRoute / StaffRoute stays in App.tsx —
 * authenticated and user-specific routes must keep serving the SPA shell.
 * Keep PRERENDER_PATHS plus DIRECTORY_PRERENDER_PATHS in sync with STATIC_ROUTES
 * in scripts/generate-sitemap.mjs (src/routes/publicMarketingRoutes.test.ts).
 */

/** Static public paths to prerender. /press/:slug is enumerated from content. */
export const PRERENDER_PATHS = [
  "/",
  "/agents",
  "/teams-brokerages",
  "/about",
  "/pricing",
  "/waitlist",
  "/press",
  "/privacy",
  "/terms",
] as const;

/**
 * The signed-out brokerage directory (#1050) and its main-city pages. Public
 * and prerendered too, but data-driven like the organization pages: the
 * prerender pass renders them AFTER the organization pass, with the list built
 * from the profiles it fetched (scripts/prerender.mjs), so they are not in the
 * static PRERENDER_PATHS loop. They render in the `.mk-app` scope, not the
 * marketing shell.
 */
export const DIRECTORY_PRERENDER_PATHS: readonly string[] = directoryPaths();

/**
 * Returned as a fragment rather than a component: <Routes> only accepts <Route>
 * or <React.Fragment> children, so this is called as `{publicMarketingRoutes()}`
 * rather than mounted as <PublicMarketingRoutes />.
 */
export function publicMarketingRoutes() {
  return (
    <>
      <Route path="/" element={<LandingPage />} />
      <Route path="/agents" element={<ForAgentsPage />} />
      <Route path="/teams-brokerages" element={<ForTeamsLandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/waitlist" element={<WaitlistPage />} />
      <Route path="/press" element={<PressPage />} />
      <Route path="/press/:slug" element={<PressReleasePage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path={DIRECTORY_BASE_PATH} element={<BrokerageDirectoryPage />} />
      <Route path={`${DIRECTORY_BASE_PATH}/:city`} element={<BrokerageDirectoryPage />} />
    </>
  );
}
